import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { getErrorMessage } from '@/utils/errorHandling'

export default function SignupPage() {
  const navigate = useNavigate()
  const signup = useAuth((state) => state.signup)
  const { t } = useTranslation(['auth', 'common'])
  const [isLoading, setIsLoading] = useState(false)

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    dob: '',
    gender: '',
    phone: '',
    address: '',
    referral_code: '',
  })

  useEffect(() => {
    // Check if user came through referral gate
    const validatedReferral = sessionStorage.getItem('validated_referral')
    if (!validatedReferral) {
      toast.error('Please enter a valid referral code first')
      navigate('/')
      return
    }

    // Set the referral code
    setFormData((prev) => ({ ...prev, referral_code: validatedReferral }))
  }, [navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('auth:passwordMismatch'))
      return
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }

    // Check age (must be 18+)
    const birthDate = new Date(formData.dob)
    const today = new Date()
    const age = today.getFullYear() - birthDate.getFullYear()
    if (age < 18) {
      toast.error('You must be at least 18 years old to register')
      return
    }

    setIsLoading(true)

    try {
      await signup({
        username: formData.username,
        password: formData.password,
        full_name: formData.full_name,
        dob: formData.dob,
        gender: formData.gender,
        phone: formData.phone || undefined,
        address: formData.address || undefined,
        referral_code: formData.referral_code,
      })

      // Clear referral from session
      sessionStorage.removeItem('validated_referral')

      toast.success(t('auth:signupSuccess'))
      navigate('/dashboard')
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-olive-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-charcoal-900">{t('auth:createAccount')}</h2>
            <p className="mt-2 text-olive-600">
              {t('auth:joinUs', { appName: t('common:app.name') })}
            </p>
            <div className="mt-4 inline-flex items-center px-4 py-2 bg-olive-100 rounded-lg">
              <span className="text-sm font-medium text-olive-800">
                Referral Code: {formData.referral_code}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username and Password */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="username"
                  className="block text-sm font-medium text-charcoal-700 mb-1"
                >
                  {t('auth:username')} *
                </label>
                <input
                  id="username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-olive-300 rounded-lg focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                  dir="auto"
                />
              </div>

              <div>
                <label
                  htmlFor="full_name"
                  className="block text-sm font-medium text-charcoal-700 mb-1"
                >
                  Full Name *
                </label>
                <input
                  id="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-olive-300 rounded-lg focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                  dir="auto"
                />
              </div>
            </div>

            {/* Password fields */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-charcoal-700 mb-1"
                >
                  {t('auth:password')} *
                </label>
                <input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 border border-olive-300 rounded-lg focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                  dir="auto"
                />
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-charcoal-700 mb-1"
                >
                  {t('auth:confirmPassword')} *
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 border border-olive-300 rounded-lg focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                  dir="auto"
                />
              </div>
            </div>

            {/* Personal Information */}
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="dob" className="block text-sm font-medium text-charcoal-700 mb-1">
                  Date of Birth *
                </label>
                <input
                  id="dob"
                  type="date"
                  value={formData.dob}
                  onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-olive-300 rounded-lg focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                />
              </div>

              <div>
                <label
                  htmlFor="gender"
                  className="block text-sm font-medium text-charcoal-700 mb-1"
                >
                  Gender *
                </label>
                <select
                  id="gender"
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-olive-300 rounded-lg focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-charcoal-700 mb-1">
                  Phone (Optional)
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-olive-300 rounded-lg focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                  dir="auto"
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-charcoal-700 mb-1">
                Address (Optional)
              </label>
              <textarea
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows={2}
                className="w-full px-4 py-3 border border-olive-300 rounded-lg focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                dir="auto"
              />
            </div>

            {/* Terms agreement */}
            <div className="bg-olive-50 rounded-lg p-4">
              <p className="text-sm text-olive-700">{t('auth:termsAgreement')}</p>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-olive-600 text-white py-3 rounded-lg font-semibold hover:bg-olive-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t('common:status.loading') : t('auth:createAccount')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-charcoal-600">
              {t('auth:alreadyHaveAccount')}{' '}
              <Link to="/login" className="text-olive-600 hover:text-olive-700 font-medium">
                {t('auth:signInHere')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
